import {
  convertExampleToCodegenInput,
  convertScenarioToCodegenInput,
  Endpoint,
  generateCode,
  Language as IgnitionLanguage,
} from '@square/ignition';
import { Example } from '@square/ignition/dist/types/compiler';
import { ExampleScenario } from '@square/ignition/dist/types/endpoint';
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { StateNode, StateNodeWithError } from '@squareup/dex-types-data-state';
import {
  CodeExampleLanguage,
  ReferenceExampleScenario,
} from '@squareup/dex-types-oas';
import { ExamplesVersionInfo } from '@squareup/dex-types-oas-path';
import { publishStateErrorState } from '@squareup/dex-utils-application-behavior-events';

import { useGraphEndpoint } from './graph';

const exampleNotFoundError = (
  endpoint?: string | undefined,
  language?: CodeExampleLanguage
): StateNodeWithError<unknown> => ({
  error: {
    data: `endpoint: ${endpoint || 'unknown'}, language: ${
      language || 'unknown'
    }`,
    error: 'Unable to find example for endpoint and language',
    originalStatus: 500,
    status: 'PARSING_ERROR',
  },
});

const endpointNotInViewError = (
  endpoint?: string | undefined
): StateNodeWithError<unknown> => ({
  error: {
    data: `endpoint: ${endpoint || 'unknown'} is not in view`,
    error: 'Unable to get example for endpoint not in view',
    originalStatus: 500,
    status: 'PARSING_ERROR',
  },
});

const getErrorOrLoading = <T extends StateNode>(node: T) => {
  if (node.isLoading || node.error) {
    return node;
  }

  return undefined;
};

const isExampleArray = (example: string | Example[]): example is Example[] => {
  return Array.isArray(example);
};

/**
 * We generate the cURL examples as a fallback in case the example.json file
 * cannot be found
 * @returns A generated cURL example
 */
const generateCurlExample = (
  endpoint: Endpoint
): ReferenceExampleScenario[] => {
  if (endpoint.exampleScenarios && endpoint.exampleScenarios.length > 0) {
    // Endpoint supports the new multiple example format
    const endpointExamples: ReferenceExampleScenario[] = [];
    for (const exampleScenario of endpoint.exampleScenarios) {
      endpointExamples.push({
        name: exampleScenario.name,
        code: generateCode(
          IgnitionLanguage.curl,
          endpoint,
          convertScenarioToCodegenInput(exampleScenario, endpoint)
        ),
        response: JSON.stringify(exampleScenario.response, null, 2),
      });
    }
    return endpointExamples;
  } else {
    // Endpoint in single-example format
    const codegenInput = convertExampleToCodegenInput(endpoint);
    return [
      {
        code: generateCode(IgnitionLanguage.curl, endpoint, codegenInput),
        name: endpoint.id,
        response: endpoint.responseBody?.example || '',
      },
    ];
  }
};

/**
 * For the current endpoint and coding language, we want to return
 * a list of possible code examples. This will be an array all the time.
 * Then, the receiver can decide what to do if there's only one example available
 * @param exampleInfo
 * @param codeLanguage
 * @returns
 */
const useExamplesForEndpoint = (
  exampleInfo: ExamplesVersionInfo,
  codeLanguage: CodeExampleLanguage
) => {
  const graphEndpointState = useGraphEndpoint();
  const examplesState = getDeveloperApi().useGetExamplesQuery(exampleInfo);

  const graphErrorLoading = getErrorOrLoading(graphEndpointState);
  if (graphErrorLoading) {
    return graphErrorLoading;
  }

  const { endpoint } = graphEndpointState;
  if (!endpoint) {
    return endpointNotInViewError();
  }

  // Must generate curl results
  if (codeLanguage === 'curl') {
    return { data: generateCurlExample(endpoint) };
  }

  const exampleErrorLoading = getErrorOrLoading(examplesState);
  if (exampleErrorLoading) {
    return exampleErrorLoading;
  }

  const { data: codeExamples } = examplesState;

  const exampleId = `${endpoint.api?.id}-api_${endpoint?.id}`;
  const example = codeExamples?.[codeLanguage]?.[exampleId];

  if (!example && codeLanguage === 'go') {
    return {
      data: [
        {
          code: '// Go examples are only available for versions after 2024-12-18',
          name: endpoint.id,
          response: endpoint.responseBody?.example || '',
        },
      ],
    };
  }

  if (!example) {
    const error = exampleNotFoundError(endpoint.id, codeLanguage);
    publishStateErrorState(error);
    return error;
  }

  // Handle multiple scenarios in the example
  if (isExampleArray(example)) {
    const scenarioMap = new Map<string, ExampleScenario>();
    endpoint.exampleScenarios?.forEach((scenario) => {
      scenarioMap.set(scenario.name, scenario);
    });

    return {
      error: undefined,
      data: example.map((ex) => {
        return {
          ...ex,
          response: JSON.stringify(scenarioMap.get(ex.name)?.response, null, 2),
        };
      }),
    };
  }

  return {
    data: [
      {
        code: example,
        name: endpoint.id,
        response: endpoint.responseBody?.example || '',
      },
    ],
  };
};

export { useExamplesForEndpoint };
